<script setup lang="ts">
import { helpers } from '@vuelidate/validators'
import { useVModel } from '@vueuse/core'

import type { Address } from '~/layers/forms/models'
import { AuxiliarMeta, Form } from '~/layers/forms/models'
import { requiredValidator } from '~/utils/validators'

interface Props {
  modelValue: Address
  validationScope?: string
  argentoInsuranceCompanyId?: number | null
  argentoInsuranceProductId?: number | null
  argentoInsurancebrokerProductId?: number | null
  lineSectionClasses?: string | Record<string, boolean>
  readonly?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  validationScope: Form.address,
  argentoInsuranceCompanyId: null,
  argentoInsuranceProductId: null,
  argentoInsurancebrokerProductId: null,
  lineSectionClasses: 'grid md:grid-cols-2 gap-4',
})

const emits = defineEmits(['update:modelValue'])
const model = useVModel(props, 'modelValue', emits)

const v$ = useValidation(
  () => ({
    line1: { required: requiredValidator },
    province: {
      selected: helpers.withMessage(
        'Seleccione una provincia de residencia.',
        () => !!model.value.province?.id
      ),
    },
    municipality: {
      selected: helpers.withMessage(
        'Seleccione un municipio de residencia.',
        () => !!model.value.municipality?.id
      ),
    },
    sector: {
      selected: helpers.withMessage(
        'Seleccione un sector de residencia.',
        () => !!model.value.sector?.id
      ),
    },
  }),
  model,
  {
    $scope: props.validationScope ?? Form.address,
    $registerAs: props.validationScope ?? Form.address,
  }
)

const { builder: dispatcherParamsBuilder, argentoInsuranceProps } =
  useSiteApiDispatcherByProductParams({
    argentoInsuranceProps: computed(() => ({
      emisorId: props.argentoInsuranceCompanyId,
      brokerProductoId: props.argentoInsurancebrokerProductId,
      productoId: props.argentoInsuranceProductId,
    })),
  })

const {
  execute: fetchCountries,
  data: countries,
  status: countriesStatus,
} = useSiteApi(
  ...dispatcherParamsBuilder({
    endpoint: '/api/Auxiliares/ByProducto/Paises',
    options: { immediate: true },
  })
)

//TODO: The country id can't be known beforehand
const country = computed(() => {
  const input = countries.value?.items
  if (!input?.length) return
  const match = ['dominican republic', 'republica dominicana', 'república dominicana']
  return input?.find((country) => {
    if (!country.nombre) return false
    //TODO: Sanitize string.
    return match.includes(country.nombre.toLowerCase())
  })?.id
})

const {
  execute: fetchProvinces,
  data: provinces,
  status: provincesStatus,
} = useSiteApi(
  ...dispatcherParamsBuilder({
    endpoint: '/api/Auxiliares/ByProducto/Provincias',
    body: computed<SiteApiSchema<'ProvinciaDispatcherSourceCriteria'>>(() => ({
      paisId: country.value,
    })),
    options: {
      immediate: true,
    },
  })
)

const {
  execute: fetchMunicipalities,
  data: municipalities,
  status: municipalitiesStatus,
} = useSiteApi(
  ...dispatcherParamsBuilder({
    endpoint: '/api/Auxiliares/ByProducto/Municipios',
    body: computed<SiteApiSchema<'MunicipioDispatcherSourceCriteria'>>(() => ({
      paisId: country.value,
      provinciaId: model.value.province?.id,
    })),
  })
)

const {
  execute: fetchSectors,
  data: sectors,
  status: sectorsStatus,
} = useSiteApi(
  ...dispatcherParamsBuilder({
    endpoint: '/api/Auxiliares/ByProducto/Sectores',
    body: computed<SiteApiSchema<'SectorDispatcherSourceCriteria'>>(() => ({
      paisId: country.value,
      provinciaId: model.value.province?.id,
      municipioId: model.value.municipality?.id,
    })),
  })
)

const _fetchProvince = async () => {
  if (props.readonly) return
  await fetchCountries()
  await fetchProvinces()
}

watch(argentoInsuranceProps, () => fetchCountries())

watch(countries, () => fetchProvinces())

watch(
  () => model.value.province?.id,
  (n, o) => {
    if (props.readonly || n === o) return
    model.value.municipality = AuxiliarMeta.load()
    if (n) fetchMunicipalities()
    if (n) {
      const province = provinces.value?.items?.find((item) => item.id === n)
      model.value.province = { id: n, code: province?.codigo, name: province?.nombre }
    }
  }
)

watch(
  () => model.value.municipality?.id,
  (n, o) => {
    if (props.readonly || n === o) return
    model.value.sector = AuxiliarMeta.load()
    if (n) fetchSectors()
    if (n) {
      const municipality = municipalities?.value?.items?.find((item) => item.id === n)
      model.value.municipality = { id: n, code: municipality?.codigo, name: municipality?.nombre }
    }
  }
)

watch(
  () => model.value.sector?.id,
  (n, o) => {
    if (props.readonly || n === o) return
    if (n) {
      const sector = sectors?.value?.items?.find((item) => item.id === n)
      model.value.sector = { id: n, code: sector?.codigo, name: sector?.nombre }
    }
  }
)
</script>

<template>
  <div
    class="col-span-full"
    :class="lineSectionClasses"
  >
    <InputField
      label="Dirección linea 1"
      :error="v$.line1?.$error"
      :error-message="v$.line1?.$errors[0]?.$message.toString()"
    >
      <PvInputText
        v-model="model.line1"
        :disabled="readonly"
      />
    </InputField>

    <InputField
      label="Dirección linea 2 (opcional)"
      :error="v$.line2?.$error"
      :error-message="v$.line2?.$errors[0]?.$message.toString()"
    >
      <PvInputText
        v-model="model.line2"
        :disabled="readonly"
      />
    </InputField>
  </div>

  <InputField
    v-if="model.province"
    label="Provincia"
    :error="v$.province?.$error"
    :error-message="v$.province?.$errors[0]?.$message.toString()"
    :loading="countriesStatus === 'pending' || provincesStatus === 'pending'"
  >
    <PvInputText
      v-if="readonly"
      v-model="model.province.name"
      no-hint
      disabled
    />
    <InputGroupRetry
      v-else
      :retry="!provinces?.items?.length"
      @retry="_fetchProvince()"
    >
      <PvSelect
        v-model="model.province.id"
        option-value="id"
        option-label="nombre"
        :options="provinces?.items ?? []"
      />
    </InputGroupRetry>
  </InputField>

  <InputField
    v-if="model.municipality"
    label="Municipio"
    :error="v$.municipality?.$error"
    :error-message="v$.municipality?.$errors[0]?.$message.toString()"
    :loading="municipalitiesStatus === 'pending'"
  >
    <PvInputText
      v-if="readonly"
      v-model="model.municipality.name"
      no-hint
      disabled
    />
    <InputGroupRetry
      v-else
      :retry="!municipalities?.items?.length && !!model.province?.id"
      @retry="fetchMunicipalities()"
    >
      <PvSelect
        v-model="model.municipality.id"
        option-value="id"
        option-label="nombre"
        :options="municipalities?.items ?? []"
        :disabled="municipalities === null || !model.province?.id"
      />
    </InputGroupRetry>
  </InputField>

  <InputField
    v-if="model.sector"
    label="Sector"
    :error="v$.sector?.$error"
    :error-message="v$.sector?.$errors[0]?.$message.toString()"
    :loading="sectorsStatus === 'pending'"
  >
    <PvInputText
      v-if="readonly"
      v-model="model.sector.name"
      no-hint
      disabled
    />
    <InputGroupRetry
      v-else
      :retry="!sectors?.items?.length && !!model.municipality?.id"
      @retry="fetchSectors()"
    >
      <PvSelect
        v-model="model.sector.id"
        option-value="id"
        option-label="nombre"
        :options="sectors?.items ?? []"
        :disabled="sectors === null || !model.municipality?.id"
      />
    </InputGroupRetry>
  </InputField>
</template>
